// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Squarebit Eye product pages / widget (store front end). The Eye software
// itself is licensed separately under the Squarebit Eye End User License
// Agreement: https://store.squarebitstudios.com/squarebit-eye/eula
// ==========================================================================

import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CodeBlock, MenuPath, Note, Tip, Warning } from '../../touchpose/ui';
import { H2, H3, Body, DocHero, DocFooter, Steps, UL_CLASS } from './shared';

// i18n contract for this page: every file name, folder name, menu path and
// node/asset name lives in a `components` slot, never inside the translated
// string — host UIs are English, so translating a menu path would make the
// instructions impossible to follow. Code blocks are siblings of the <Trans>,
// never children.
//
// The page is ordered by how much work each host asks of you, not
// alphabetically: the two hosts with a one-call script first, then the host
// that needs no script at all, then the shared USD route, then the renderers
// that only need the material pointed at them.
const M = (s: string) => <MenuPath>{s}</MenuPath>;
const LINK = 'text-blue-link no-underline font-medium';

const GettingStartedDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary max-w-[760px]">
      <DocHero
        title={t('eyeDocs.gettingStarted.title')}
        intro={t('eyeDocs.gettingStarted.intro')}
      />

      <Note label={t('eyeDocs.gettingStarted.projectorLabel')}>
        <Trans
          i18nKey="eyeDocs.gettingStarted.projectorNote"
          components={{ b: <strong /> }}
        />
      </Note>

      {/* ------------------------------------------------------------ Maya */}
      <H2 id="maya">{t('eyeDocs.layout.mayaArnold')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.gettingStarted.mayaIntro"
          components={{ s: M('setup_eye_on_selection.py') }}
        />
      </Body>
      <Steps>
        <>{t('eyeDocs.gettingStarted.mayaStep1')}</>
        <><Trans
            i18nKey="eyeDocs.gettingStarted.mayaStep2"
            components={{ p: M('Python') }}
          /></>
      </Steps>
      <CodeBlock>{`from setup_eye_on_selection import setup_eye_on_selection

# the whole eyeball mesh, or just the eye faces on a head
nodes = setup_eye_on_selection(name="charL", aim_at=(0, 160, 50))`}</CodeBlock>
      <Body>{t('eyeDocs.gettingStarted.mayaResult')}</Body>
      <Tip label={t('eyeDocs.gettingStarted.mayaAimTipLabel')}>
        {t('eyeDocs.gettingStarted.mayaAimTip')}
      </Tip>

      {/* --------------------------------------------------------- Blender */}
      <H2 id="blender">{t('eyeDocs.layout.blender')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.gettingStarted.blenderIntro"
          components={{ s: M('setup_eye_on_selection.py') }}
        />
      </Body>
      <Steps>
        <><Trans
            i18nKey="eyeDocs.gettingStarted.blenderStep1"
            components={{ e: M('Edit Mode') }}
          /></>
        <><Trans
            i18nKey="eyeDocs.gettingStarted.blenderStep2"
            components={{ w: M('Scripting') }}
          /></>
      </Steps>
      <CodeBlock>{`import sys; sys.path.append(r"<install>/blender/examples")
from setup_eye_on_selection import setup_eye_on_selection

nodes = setup_eye_on_selection(name="charL", aim_at=(0, -2, 1.6))`}</CodeBlock>
      <Body>{t('eyeDocs.gettingStarted.blenderResult')}</Body>

      {/* ---------------------------------------------------------- Unreal */}
      <H2 id="unreal">{t('eyeDocs.layout.unreal')}</H2>
      <Body>{t('eyeDocs.gettingStarted.unrealIntro')}</Body>
      <Steps>
        <><Trans
            i18nKey="eyeDocs.gettingStarted.unrealStep1"
            components={{ m: M('Squarebit ▸ Bake Eye Section Radius') }}
          /></>
        <>{t('eyeDocs.gettingStarted.unrealStep2')}</>
        <>{t('eyeDocs.gettingStarted.unrealStep3')}</>
      </Steps>
      <Tip label={t('eyeDocs.gettingStarted.unrealBoneTipLabel')}>
        {t('eyeDocs.gettingStarted.unrealBoneTip')}
      </Tip>
      <Body>
        <Trans
          i18nKey="eyeDocs.gettingStarted.unrealLook"
          components={{ m: M('Squarebit ▸ Export Eye Look') }}
        />
      </Body>

      {/* ------------------------------------------------------------- USD */}
      <H2 id="usd">{t('eyeDocs.layout.usd')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.gettingStarted.usdIntro"
          components={{ a: M('SquarebitEye.usda') }}
        />
      </Body>
      <CodeBlock>{`python usd/examples/place_eye_in_stage.py character.usda \\
    --under /char --name eye_L \\
    --at 0.03 1.70 -0.02 --radius 0.0125 --aim-at 0 1.7 -2`}</CodeBlock>
      <Body>{t('eyeDocs.gettingStarted.usdUnits')}</Body>
      <Warning label={t('eyeDocs.gettingStarted.usdUnitsWarningLabel')}>
        {t('eyeDocs.gettingStarted.usdUnitsWarning')}
      </Warning>

      {/* --------------------------------------------------------- Houdini */}
      <H2 id="houdini">{t('eyeDocs.layout.houdini')}</H2>
      <Body>{t('eyeDocs.gettingStarted.houdiniIntro')}</Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.gettingStarted.houdiniSolaris"
            components={{ b: <strong /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.gettingStarted.houdiniGl"
            components={{ b: <strong />, h: M('sbe_eye_glsl.hda') }}
          />
        </li>
      </ul>
      <Warning label={t('eyeDocs.gettingStarted.houdini22WarningLabel')}>
        {t('eyeDocs.gettingStarted.houdini22Warning')}
      </Warning>

      {/* ------------------------------------------------------- Omniverse */}
      <H2 id="omniverse">{t('eyeDocs.layout.omniverse')}</H2>
      <Body>{t('eyeDocs.gettingStarted.omniverseIntro')}</Body>
      <Tip label={t('eyeDocs.gettingStarted.omniverseDeformerTipLabel')}>
        {t('eyeDocs.gettingStarted.omniverseDeformerTip')}
      </Tip>

      {/* ---------------------------------------------- renderers/backends */}
      <H2 id="renderers">{t('eyeDocs.gettingStarted.renderersTitle')}</H2>
      <Body>{t('eyeDocs.gettingStarted.renderersIntro')}</Body>

      <H3 id="arnold">Arnold</H3>
      <Body>{t('eyeDocs.gettingStarted.arnold')}</Body>

      <H3 id="materialx">{t('eyeDocs.layout.materialx')}</H3>
      <Body>{t('eyeDocs.gettingStarted.materialx')}</Body>

      <H3 id="mdl">MDL</H3>
      <Body>{t('eyeDocs.gettingStarted.mdl')}</Body>

      <H3 id="storm">Hydra Storm</H3>
      <Body>{t('eyeDocs.gettingStarted.storm')}</Body>

      {/* ------------------------------------------------------------- web */}
      <H2 id="web">{t('eyeDocs.layout.web')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.gettingStarted.webIntro"
          components={{
            w: <Link to="/squarebit-eye" className={LINK} />
          }}
        />
      </Body>

      {/* ----------------------------------------------------- what's next */}
      <H2 id="next">{t('eyeDocs.gettingStarted.nextTitle')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.gettingStarted.next"
          components={{
            p: <Link to="/squarebit-eye/docs/parameters" className={LINK} />,
            t: <Link to="/squarebit-eye/docs/textures" className={LINK} />,
            r: <Link to="/squarebit-eye/docs/rigging" className={LINK} />,
            i: <Link to="/squarebit-eye/docs/installation" className={LINK} />
          }}
        />
      </Body>

      <DocFooter current="/squarebit-eye/docs/getting-started" />
    </article>
  );
};

export default GettingStartedDoc;
